// @file init and helper functions for command bar

import { captureException } from '@@/bits/error_tracker'
import { isAppUsing } from '@@/bits/flip'
import window from '@@/bits/global'
import type { User } from '@@/types'
import { init as commandbarInit } from 'commandbar'

// defines valid user properties for CommandBar
enum CommandBarUserPropertyType {
  HasCreatedSlideshowBefore = 'has_created_slideshow_before',
}

const COMMAND_BAR_ORG_ID = '88b7c3d1'
const ENABLED_PRODUCTS: Array<'nudges' | 'spotlight' | 'checklists' | 'help_hub'> = ['nudges']
const APP = 'mozart'
const MS_PER_DAY = 1000 * 60 * 60 * 24

function initCommandBar(): void {
  if (!isAppUsing('commandBar')) return

  if (window.CommandBar == null) {
    commandbarInit(COMMAND_BAR_ORG_ID)
  }
}

function bootCommandBarWithUser(currentUser: Partial<User>): void {
  if (!isAppUsing('commandBar')) return

  initCommandBar()

  if (currentUser == null || currentUser.id == null) {
    // At the very least need user_id to boot the command bar
    return
  }

  let environment = process.env.NODE_ENV ?? 'development'

  if (window.location.hostname.includes('.padlet.io')) {
    environment = 'staging'
  }

  try {
    const userProperties = {
      id: currentUser.id.toString(),
      name: currentUser.name ?? '',
      email: currentUser.email ?? '',
      tenantId: currentUser.tenant_id?.toString() ?? '',
      role: currentUser.role ?? '',
      tenantType: currentUser.tenant_type ?? '',
      paying: currentUser.paying ?? false,
      registeredAt: currentUser.registered_at?.toLocaleString() ?? '',
      environment,
      daysSinceRegistration: -1,
      app: APP,
      lang: currentUser.lang ?? '',
    }
    if (currentUser.registered_at != null) {
      const daysSinceRegistration = Math.floor(
        (Date.now() - new Date(currentUser.registered_at).getTime()) / MS_PER_DAY,
      )
      userProperties.daysSinceRegistration = daysSinceRegistration
    }

    const userIdentifier = `${APP}_${environment}_${currentUser.id}`
    void window.CommandBar.boot(userIdentifier, userProperties, {
      products: ENABLED_PRODUCTS,
    })
  } catch (error) {
    captureException(error)
  }
}

function setUserProperty(userProperty: CommandBarUserPropertyType, value: any): void {
  if (!isAppUsing('commandBar') || window.CommandBar == null) return

  const userProperties = {
    [userProperty]: value,
  }

  void window.CommandBar.setUserProperties(userProperties)
}

function trackEvent(event: string, properties: Record<string, string>): void {
  if (!isAppUsing('commandBar') || window.CommandBar == null) return

  window.CommandBar.trackEvent(event, properties)
}

export { bootCommandBarWithUser, CommandBarUserPropertyType, initCommandBar, setUserProperty, trackEvent }
